import React, { useState } from "react";
import {
  ControlsVerticalMenu,
  ParametersTable,
  Button,
} from "components/shared";
import { Search, Icon } from "semantic-ui-react";
import { PARAM_UPDATE } from "../../constants";
import { toast } from "react-toastify";
import AdvancedControlButtons from "containers/advanced/AdvancedControlButtons";
import ActionLogs from "containers/advanced/ActionLogs";
import SettingsControlButtons from "./SettingsControlButtons";
import Webpages from "./Webpages";
import styled from "styled-components";
import useAxios from "axios-hooks";

const Wrapper = styled.div``;

const ControlsMenuAdvanced = ({
  windTurbine,
  turbineParams,
  liveParams,
  dispatch,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const [{ data }, getRefresh] = useAxios({
    url: `/advanced`,
    params: {
      windTurbineIdentifier: windTurbine.identifier,
      windTurbineType: windTurbine.type.id,
      fetchPolicy: "no-cache",
    },
  });

  // Schema-driven turbines (MQTT bridge) go through the same authenticated
  // /mqttCommand route the coil/action buttons use (see
  // containers/common/ExecuteActionButton.js) - it publishes the write,
  // waits for the device's ack, and logs to ActionLog in one call. This
  // replaces the legacy writeParam()'s direct, unauthenticated
  // /iotgateway/write call, which has no effect on an MQTT-fed turbine
  // since KEPServer never sees that device at all.
  const [, executeMqttCommand] = useAxios(
    {
      url: `/windTurbines/${windTurbine.id}/mqttCommand`,
      method: "PUT",
    },
    { manual: true }
  );

  const handleSearchChange = (e, { value }) => {
    setSearchQuery(value);
  };

  const onIconClick = () => {
    if (searchQuery && searchQuery.length > 0) {
      setSearchQuery("");
    }
  };

  const onEditParameter = async (param, newValue) => {
    const paramDef = turbineParams.find((p) => p.number === param.number);
    // Send the real-world (unscaled) value, same units the user typed and
    // the same units liveParams already holds (the server's live-params
    // selector divides raw device values by this same scale before they
    // ever reach the frontend). The firmware does its own multiply-by-scale
    // before writing the raw register - scaling here too double-applied it,
    // e.g. 600s * 100 sent, then firmware * 100 again = 60000 written raw,
    // overflowing the 16-bit register into a garbage value on readback.
    try {
      await executeMqttCommand({ data: { tag: param.dbname, value: newValue } });
      toast.success("TMC Parameter edited successfully");
      dispatch({
        type: PARAM_UPDATE,
        payload: {
          windTurbineId: windTurbine.id,
          paramId: paramDef.dbname,
          newValue,
        },
      });
      // Refresh only after the write's ack confirms it actually landed -
      // firing this beforehand (as the old code did) raced the read against
      // the write and could show the pre-edit value.
      getRefresh();
    } catch (error) {
      toast.error("Failed to edit parameter");
    }
  };

  const parametersData = turbineParams
    .map((p) => {
      let scale = p.scale || 1;
      let value =
        liveParams[p.dbname] !== undefined
          ? (p.value = liveParams[p.dbname])
          : liveParams.default;
      // min/max/default aren't documented for every register source (e.g.
      // RAGO's reverse-engineered map has none) - divide only when present,
      // rather than turning "undefined" into NaN and silently losing the
      // Save button's range validation below.
      const scaled = (v) => (v === undefined || v === null ? undefined : v / scale);
      return {
        dbname: p.dbname,
        number: p.number,
        // Legacy (pre-schema) turbines only ever had one such value and
        // ControlsMenu never split it into two - schema-driven ones carry
        // the real Modbus address separately (see selectors/turbineParams.js).
        address: p.address !== undefined ? p.address : p.number,
        name: p.name,
        value,
        min: scaled(p.min),
        max: scaled(p.max),
        unit: p.unit,
        default: scaled(p.default)
      };
    })
    .filter(
      (d) => d.name.toLowerCase().indexOf(searchQuery.toLowerCase()) > -1
    );

  const CONTROLS = [
    {
      key: "ADVANCED CONTROLS",
      title: "ADVANCED CONTROLS",
      content: <AdvancedControlButtons windTurbine={windTurbine} />,
    },
    {
      key: "PARAMETERS",
      title: "PARAMETERS",
      content: (
        <Wrapper>
          <Button
            style={{ margin: "0rem 0rem 1rem 0rem" }}
            onClick={() => {
              getRefresh();
            }}
          >
            Refresh parameters
          </Button>

          <Search
            loading={false}
            onSearchChange={handleSearchChange}
            showNoResults={false}
            value={searchQuery}
            style={{ margin: "0rem 0rem 1rem 0rem" }}
            icon={
              <Icon
                link
                name={
                  searchQuery && searchQuery.length > 0 ? "cancel" : "search"
                }
                onClick={onIconClick}
              />
            }
          />

          {parametersData ? (
            <ParametersTable
              key="parameters"
              boldIndexes={[1, 2]}
              paginated
              editable
              numberOfItemsPerPage={1000}
              params={parametersData}
              columnNames={["Name", "Value", "Unit"]}
              columnKeys={["name", "value", "unit"]}
              cellStyle={{ padding: "4px 50px 4px 50px" }}
              onEditParameter={onEditParameter}
            />
          ) : (
            "Loading..."
          )}
        </Wrapper>
      ),
    },
    {
      key: "SETTINGS",
      title: "SETTINGS",
      content: (
        <Wrapper>
          <SettingsControlButtons />
        </Wrapper>
      ),
    },
    {
      key: "ACTION LOGS",
      title: "ACTION LOGS",
      content: (
        <Wrapper>
          <ActionLogs />
        </Wrapper>
      ),
    },
    {
      key: "WEBPAGES",
      title: "WEBPAGES",
      content: (
        <Wrapper>
          <Webpages windTurbine={windTurbine} />
        </Wrapper>
      ),
    },
  ];

  return <ControlsVerticalMenu controls={CONTROLS} windTurbine={windTurbine} />;
};

ControlsMenuAdvanced.propTypes = {};

ControlsMenuAdvanced.defaultProps = {};

export default ControlsMenuAdvanced;
